import React, { useState, useEffect, useCallback } from 'react';
import { Search, Grid, List, Loader2, AlertCircle, Users, Filter, X, Star } from 'lucide-react';
import { VendorPublicProfile, VendorListResponse } from '../types';
import { vendorApi } from '../services/api';
import VendorCard from './VendorCard';
import Pagination from './Pagination';
import SEOHead from './SEOHead';
import { extractApiError } from '../utils/errorUtils';
import { toast } from '../utils/toast';
import { useFeatureFlag } from '../hooks/useFeatureFlag';

// Common product types for filtering
const FILTER_PRODUCT_TYPES = [
  'Jewelry', 'Pottery', 'Paintings', 'Photography', 'Woodwork',
  'Textiles', 'Candles', 'Soap', 'Leather Goods', 'Glass Art',
  'Metalwork', 'Prints', 'Sculptures', 'Clothing', 'Accessories',
  'Home Decor', 'Paper Goods', 'Food', 'Crafts', 'Mixed Media'
];

export default function VendorListPage() {
  const [vendors, setVendors] = useState<VendorPublicProfile[]>([]);
  const [totalVendors, setTotalVendors] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [minRating, setMinRating] = useState<number | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showFilters, setShowFilters] = useState(false);
  const [followedVendorIds, setFollowedVendorIds] = useState<number[]>([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Check vendor_following feature flag (Issue #1472)
  const { isEnabled: isFollowingEnabled } = useFeatureFlag('vendor_following');

  const loadVendors = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const params: { product_types?: string; min_rating?: number; page: number; page_size: number } = {
        page: currentPage,
        page_size: pageSize,
      };

      if (selectedTypes.length > 0) {
        params.product_types = selectedTypes.join(',');
      }

      if (minRating !== null && minRating > 0) {
        params.min_rating = minRating;
      }

      const response: VendorListResponse = await vendorApi.getVendors(params);

      // Filter by search query client-side (API doesn't support search)
      // Note: When search is active, pagination shows filtered results from current page only
      let filteredVendors = response.items;
      const isSearchActive = searchQuery.trim().length > 0;
      if (isSearchActive) {
        const query = searchQuery.toLowerCase();
        filteredVendors = filteredVendors.filter(v =>
          v.business_name.toLowerCase().includes(query) ||
          v.description?.toLowerCase().includes(query) ||
          v.product_types?.some(t => t.toLowerCase().includes(query))
        );
      }

      setVendors(filteredVendors);
      // When search is active, show filtered count to avoid misleading pagination
      // Set total to filtered count and disable pagination (hide it)
      if (isSearchActive) {
        setTotalVendors(filteredVendors.length);
        setTotalPages(1); // Hide pagination when searching (client-side limitation)
      } else {
        setTotalVendors(response.total);
        setTotalPages(response.total_pages);
      }
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to load vendors:', apiError);
      setError(apiError.message);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, selectedTypes, minRating, searchQuery]);

  useEffect(() => {
    loadVendors();
  }, [loadVendors]);

  // Load followed vendors on mount (if authenticated and feature is enabled) (Issue #1472)
  useEffect(() => {
    const checkAuthAndLoadFollowed = async () => {
      // Only load followed vendors if feature flag is enabled
      if (!isFollowingEnabled) {
        return;
      }

      const token = localStorage.getItem('access_token');
      if (token) {
        setIsAuthenticated(true);
        try {
          const { vendor_ids } = await vendorApi.getFollowedVendors();
          setFollowedVendorIds(vendor_ids);
        } catch (err: unknown) {
          // User not authenticated or error - silently continue
          console.debug('Could not load followed vendors:', err);
        }
      }
    };

    checkAuthAndLoadFollowed();
  }, [isFollowingEnabled]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedTypes, minRating, searchQuery]);

  const handleTypeToggle = (type: string) => {
    setSelectedTypes(prev =>
      prev.includes(type)
        ? prev.filter(t => t !== type)
        : [...prev, type]
    );
  };

  const clearFilters = () => {
    setSelectedTypes([]);
    setMinRating(null);
    setSearchQuery('');
  };

  const hasActiveFilters = selectedTypes.length > 0 || minRating !== null || searchQuery.trim().length > 0;

  // Handle follow/unfollow vendor (Issue #1198)
  const handleBookmark = async (vendorId: number) => {
    if (!isAuthenticated) {
      toast.error('Please log in to follow vendors');
      return;
    }

    const isCurrentlyFollowed = followedVendorIds.includes(vendorId);

    try {
      if (isCurrentlyFollowed) {
        await vendorApi.unfollowVendor(vendorId);
        setFollowedVendorIds(prev => prev.filter(id => id !== vendorId));
        toast.success('Vendor unfollowed');
      } else {
        await vendorApi.followVendor(vendorId);
        setFollowedVendorIds(prev => [...prev, vendorId]);
        toast.success('Vendor followed!');
      }
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      toast.error(apiError.message || 'Failed to update vendor following');
      console.error('Failed to follow/unfollow vendor:', apiError);
    }
  };

  // Generate SEO content
  const seoTitle = 'Vendor Directory';
  let seoDescription = 'Browse local artists and vendors offering handmade goods, artisan products, and unique crafts';
  if (selectedTypes.length > 0) {
    seoDescription = `Browse vendors offering ${selectedTypes.join(', ')}. ${seoDescription}`;
  }
  const seoKeywords = ['vendor directory', 'local artists', 'artisans', 'handmade', 'crafts', ...selectedTypes].join(', ');

  return (
    <>
      <SEOHead
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        canonicalUrl="https://localartistfairs.com/vendors"
      />
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Users className="w-8 h-8 text-secondary" />
            Vendor Directory
          </h1>
          <p className="text-gray-600 mt-2">
            Browse local artists and vendors
          </p>
        </div>

      {/* Search and Filter Bar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search vendors by name or product..."
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
            />
          </div>

          {/* Filter Toggle (Mobile) */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="md:hidden flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Filter className="w-4 h-4" />
            Filters
            {(selectedTypes.length > 0 || minRating !== null) && (
              <span className="bg-primary text-white text-xs px-2 py-0.5 rounded-full">
                {selectedTypes.length + (minRating !== null ? 1 : 0)}
              </span>
            )}
          </button>

          {/* View Toggle */}
          <div className="flex border border-gray-300 rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 ${viewMode === 'grid' ? 'bg-primary text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              title="Grid view"
            >
              <Grid className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 ${viewMode === 'list' ? 'bg-primary text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
              title="List view"
            >
              <List className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Filters (Desktop - always visible, Mobile - toggle) */}
        <div className={`mt-4 space-y-4 ${showFilters ? 'block' : 'hidden md:block'}`}>
          {/* Rating Filter (Issue #1198) */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Minimum rating:</span>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="text-sm text-secondary hover:text-primary-dark flex items-center gap-1"
                >
                  <X className="w-3 h-3" />
                  Clear all
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {[4, 3, 2].map(rating => (
                <button
                  key={rating}
                  onClick={() => setMinRating(minRating === rating ? null : rating)}
                  className={`px-3 py-1.5 rounded-full text-sm transition-colors flex items-center gap-1 ${
                    minRating === rating
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Star className={`w-3.5 h-3.5 ${minRating === rating ? 'fill-current' : ''}`} />
                  {rating}+ stars
                </button>
              ))}
            </div>
          </div>

          {/* Product Type Filter */}
          <div>
            <span className="text-sm font-medium text-gray-700">Filter by product type:</span>
            <div className="flex flex-wrap gap-2 mt-2">
              {FILTER_PRODUCT_TYPES.map(type => (
                <button
                  key={type}
                  onClick={() => handleTypeToggle(type)}
                  className={`px-3 py-1 rounded-full text-sm transition-colors ${
                    selectedTypes.includes(type)
                      ? 'bg-primary text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Results Summary */}
      <div className="flex items-center justify-between mb-4">
        <p className="text-gray-600">
          {isLoading ? (
            'Loading...'
          ) : searchQuery.trim() ? (
            <>
              Found <span className="font-medium">{vendors.length}</span> matching vendors
              <span className="text-gray-400 text-sm ml-2">(searching current page)</span>
            </>
          ) : (
            <>
              Showing <span className="font-medium">{vendors.length}</span> of{' '}
              <span className="font-medium">{totalVendors}</span> vendors
            </>
          )}
        </p>
      </div>

      {/* Error State */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-red-700">{error}</p>
          </div>
          <button
            onClick={() => loadVendors()}
            className="text-red-600 hover:text-red-700 text-sm font-medium"
          >
            Retry
          </button>
        </div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-secondary" />
        </div>
      )}

      {/* Empty State */}
      {!isLoading && vendors.length === 0 && (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No vendors found</h3>
          <p className="text-gray-600 mb-4">
            {hasActiveFilters
              ? 'Try adjusting your filters or search query'
              : 'Be the first to create a vendor profile!'}
          </p>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="text-secondary hover:text-primary-dark font-medium"
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      {/* Vendor Grid/List */}
      {!isLoading && vendors.length > 0 && (
        <>
          <div className={
            viewMode === 'grid'
              ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'
              : 'space-y-4'
          }>
            {vendors.map(vendor => (
              <VendorCard
                key={vendor.id}
                vendor={vendor}
                onBookmark={isFollowingEnabled && isAuthenticated ? handleBookmark : undefined}
                isBookmarked={isFollowingEnabled && followedVendorIds.includes(vendor.id)}
              />
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="mt-8">
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                pageSize={pageSize}
                totalItems={totalVendors}
                onPageChange={setCurrentPage}
                onPageSizeChange={setPageSize}
                itemName="vendors"
              />
            </div>
          )}
        </>
      )}
    </div>
    </>
  );
}
